import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import router from '@/router';
import i18n from '@/plugins/vue-i18n';
import { useAuthStore } from '@/stores/auth.store';
import { useOrgStore } from '@/stores/organisations.store';
import { loggedInUserOrgList, selectedOrgId } from '@/common/utils/role.utils';
import { useCan } from '@/composables/use-can';
import { Actions, Subjects } from '@boqnet/permissions';
import { setNewOrg } from '@/common/utils/user.utils';
import { BOQRoles } from '@/types/api/roles.types';
import type { UserOrganisation } from '@/types/api/auth.types';

// Shared by NavMenu.vue and MobileNavigation.vue - each hosts its own switcher instance.
export const useOrganisationSwitcher = () => {
  const { t } = i18n.global;
  const authStore = useAuthStore();
  const orgStore = useOrgStore();
  const { loggedInUser } = storeToRefs(authStore);

  const { can } = useCan();
  const showCreateNew = computed(() => can(Actions.CREATE, Subjects.ORGANISATION));
  const createNewLabel = computed(() => t('navigation.createNewOrg'));

  const onSelectOrg = async (org: Partial<UserOrganisation>) => {
    if (loggedInUser.value?.selectedOrg?.id === org.id) return;
    await setNewOrg(org as UserOrganisation);
  };

  const onSetPrimary = (id: string) => {
    orgStore.setPrimaryOrganisation(id);
  };

  const onCreateNew = () => {
    router.push({ name: 'create-org', params: { organisationId: selectedOrgId.value } });
  };

  return {
    orgs: loggedInUserOrgList,
    selectedOrgId,
    showCreateNew,
    createNewLabel,
    onSelectOrg,
    onSetPrimary,
    onCreateNew,
  };
};
